// src/contexts/AppointmentContext.jsx
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, buildVideoConferenceUrl } from '../utilities/api';
import { useUser } from './UserContext';
import { utcStringToDate } from '../utilities/utilities';

// Create the context
const AppointmentContext = createContext();

// Export the useAppointment hook
export const useAppointment = () => useContext(AppointmentContext);

export const AppointmentProvider = ({ children }) => {
  const { user, isLoggedIn } = useUser();
  const [appointments, setAppointments] = useState([]);
  const [currentAppointment, setCurrentAppointment] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const refreshAppointments = useCallback(async () => { 
    try {
      setIsLoading(true);
      const response = await api.getAppts();
      if (!response.error) {
        const converted = response.data
          .map(appt => ({
            ...appt,
            date_appt: new Date(utcStringToDate(appt.date_appt))
          }))
          .sort((a, b) => new Date(b.date_appt) - new Date(a.date_appt));
        setAppointments(converted);
        setError(null);
        return converted;
      }
      return [];
    } catch (err) {
      setError(`Failed to fetch appointments ${err.message}`);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const bookAppointment = useCallback(async (appointmentData) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await api.bookAppointment(appointmentData);
      if (response.error) {
        // Pass the structured response through so the caller (SchedulePage)
        // can branch on `response.error === 'contact_required'` and use the
        // detail fields (preferred_contact, email_ok, phone_ok) to route the
        // user to the right Profile tab.
        setError(`Error booking appointment: ${response.error}`);
        return response;
      }
      await refreshAppointments();
      return response;
    } catch (err) {
      setError(`Error booking appointment: ${err.message}`);
      return ({error: `Error booking appointment: ${err.message}`});
    } finally {
      setIsLoading(false);
    }
  },[refreshAppointments]);

  /**
   * Reserve an appointment slot before payment. Returns { success, appointment_id,
   * expires_at } on success; structured error response on failure (including
   * contact_required shape for 422s).
   */
  const reserveAppointmentPending = useCallback(async (data) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await api.reserveAppointmentPending(data);
      if (response.error) {
        setError(`Error reserving appointment: ${response.error}`);
        return response;
      }
      return response;
    } catch (err) {
      setError(`Error reserving appointment: ${err.message}`);
      return { error: `Error reserving appointment: ${err.message}` };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Cancel a pending appointment (idempotent). Used for explicit user-initiated
   * cancellations. For unload paths use api.beaconCancelPendingAppointment()
   * directly from SchedulePage.
   */
  const cancelPendingAppointment = useCallback(async (appointmentId) => {
    setError(null);
    try {
      const response = await api.cancelPendingAppointment({ appointment_id: appointmentId });
      if (response.error) {
        setError(`Error cancelling pending appointment: ${response.error}`);
        return response;
      }
      return response;
    } catch (err) {
      setError(`Error cancelling pending appointment: ${err.message}`);
      return { error: `Error cancelling pending appointment: ${err.message}` };
    }
  }, []);

  const cancelAppointment = useCallback(async (appointmentId) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await api.cancelAppointment({ appointment_id: appointmentId });
      if (response.error) {
        throw new Error(response.error);
      }
      await refreshAppointments();
      return response;
    } catch (err) {
      setError(`Error canceling appointment: ${err.message}`);
      return ({error: `Error canceling appointment: ${err.message}`});
    } finally {
      setIsLoading(false);
    }
  },[refreshAppointments]);

  const joinAppointment = async (appointmentId) => {
    setError(null);
    setIsLoading(true);
    try {
      const confData = await api.attendAppointment({ userid: user.userid, appointment_id: appointmentId });
      if (confData?.error) {
        throw new Error(`Error joining conference: ${confData.error}`);
      } else if (confData?.opentoktoken) {
          const otUrl = buildVideoConferenceUrl(confData.opentoktoken);
          return ({success : true, opentokurl: otUrl});
      } else {
        throw new Error(`Failed to get conference details.`);
      }
    } catch (err) {
      setError(`Error joining appointment: ${err.message}`);
      return ({error: `Error joining appointment: ${err.message}`});
    } finally {
      setIsLoading(false);
    }
  };

  const getAppointmentById = useCallback((apptId) => {
    return appointments.find((appt) => appt.appointment_id === apptId) || null;
  }, [appointments]);

  const setAppointment = (apptId, appointmentList = appointments) => {
      const appointment = appointmentList.find((appt) => String(appt.appointment_id) === String(apptId)) || null;
      if (!appointment) {
        setCurrentAppointment(null);
        return false;
      }
      setCurrentAppointment(appointment);
      return true;
  };

  useEffect(() => {
    if (isLoggedIn) refreshAppointments();
  }, [isLoggedIn, refreshAppointments]);

  const contextValue = {
    appointments,
    currentAppointment,
    isLoading,
    error,
    setAppointment,
    getAppointmentById,
    refreshAppointments,
    bookAppointment,
    reserveAppointmentPending,
    cancelPendingAppointment,
    cancelAppointment,
    joinAppointment,
  };

  return (
    <AppointmentContext.Provider value={contextValue}>
      {children}
    </AppointmentContext.Provider>
  );
};
